import React from 'react';
import { useBattle } from '../context';
import { getDirectAttackStatus } from '../../engine/keywordAbilities';

import type { MonsterInstance, CardData, HandCard, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';

/** ActionFooter Props */
interface ActionFooterProps {
  /** チャージ使用済みフラグ */
  chargeUsedThisTurn: boolean;

  // キーワード能力ヘルパー
  canActivateSourei: (monster: MonsterInstance, hand: HandCard[]) => boolean;
  getSoureiEffect: (cardId: string) => { description?: string } | null;
  canActivateMiraiYochi: (monster: MonsterInstance, phase: number) => boolean;
  canActivateGigen: (card: CardData | MonsterInstance | HandCard, fieldOrPhase: (MonsterInstance | null)[] | number) => boolean;
  canActivateHengenmi: (monster: MonsterInstance, activeSP: number, deck: CardData[], phase: number) => boolean;

  // トリガー
  getCardMainPhaseTriggers: (card: CardData, currentPlayer: PlayerNumber) => any[];
  createTriggerContext: (monster: MonsterInstance, slotIndex: number) => any;
  activateTrigger: (trigger: any, context: any) => void;

  // 魔法カード
  useMagicCard: () => void;

  // 墓地発動
  activatableGraveyardCards: CardWithId[];
  activateGraveyardCard: (card: CardData) => void;

  // ダイレクトアタック
  handleDirectAttack: () => void;
  canIgnoreBlockWithIatsuko: () => boolean;

  // 深海領域
  handleSubmerge?: (card: HandCard) => void;
}

/**
 * ActionFooter コンポーネント
 *
 * デュエル画面下部のアクションバー
 * - 左側: スクロールエリア（技、魔法、犠現、刹那詠唱など）
 * - 右側: 固定エリア（フェイズ遷移ボタン）
 */
const ActionFooter: React.FC<ActionFooterProps> = ({
  chargeUsedThisTurn,
  canActivateSourei,
  getSoureiEffect,
  canActivateMiraiYochi,
  canActivateGigen,
  canActivateHengenmi,
  getCardMainPhaseTriggers,
  createTriggerContext,
  activateTrigger,
  useMagicCard,
  activatableGraveyardCards,
  activateGraveyardCard,
  handleDirectAttack,
  canIgnoreBlockWithIatsuko,
  handleSubmerge,
}) => {
  const {
    currentPlayer,
    phase,
    isMobile,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1ActiveSP,
    p2ActiveSP,
    selectedHandCard,
    selectedFieldMonster,
    attackingMonster,
    executeSkill,
    chargeSP,
    nextPhase,
    gigen,
    miraiYochi,
    sourei,
    hengenmi,
    setsuna,
    deepSea,
    turn,
  } = useBattle();

  // キーワード能力関数
  const { startGigenActivation } = gigen;
  const { activateMiraiYochi } = miraiYochi;
  const { activateSourei } = sourei;
  const { activateHengenmi } = hengenmi;

  // 刹那詠唱関連
  const {
    setsunaPendingActivation,
    startSetsunaMagicMode,
    canUseSetsunaMagic,
  } = setsuna;

  // 深海領域関連
  const {
    canSubmergeCard,
    getPlayerDeepSeaZone,
    setShowDeepSeaModal,
  } = deepSea;

  // プレイヤーデータ取得
  const currentField = currentPlayer === 1 ? p1Field : p2Field;
  const currentHand = currentPlayer === 1 ? p1Hand : p2Hand;
  const currentDeck = currentPlayer === 1 ? p1Deck : p2Deck;
  const currentActiveSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
  const opponentField = currentPlayer === 1 ? p2Field : p1Field;

  // 選択中のモンスター
  const selectedMonster = selectedFieldMonster !== null ? currentField[selectedFieldMonster] : null;

  // ========================================
  // ボタンスタイル
  // ========================================
  const baseButtonStyle: React.CSSProperties = {
    flexShrink: 0,
    padding: isMobile ? '8px 12px' : '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: isMobile ? '12px' : '13px',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  };

  const phaseButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    minWidth: isMobile ? '100px' : '120px',
    textAlign: 'center',
  };

  const getButtonStyle = (
    gradient: string,
    disabled: boolean = false
  ): React.CSSProperties => ({
    ...baseButtonStyle,
    background: disabled
      ? 'linear-gradient(135deg, #666 0%, #888 100%)'
      : gradient,
    color: '#fff',
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  // ========================================
  // スクロールエリアのボタン群をレンダリング
  // ========================================
  const renderScrollAreaButtons = () => {
    const buttons: React.ReactNode[] = [];

    // メインフェイズ中のモンスター選択時
    if (phase === 2 && selectedMonster && selectedFieldMonster !== null) {
      const monster = selectedMonster;
      const triggers = getCardMainPhaseTriggers(monster, currentPlayer);

      // 技を持つモンスターの場合
      if (monster.basicSkill || monster.advancedSkill) {
        // 基本技
        if (monster.basicSkill) {
          const canUseBasic = monster.charges && monster.charges.length >= 1 && !monster.usedSkillThisTurn;
          buttons.push(
            <button
              key="basic-skill"
              onClick={() => executeSkill(selectedFieldMonster, 'basic')}
              disabled={!canUseBasic}
              style={getButtonStyle('linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', !canUseBasic)}
            >
              基本技 ({monster.charges?.length || 0}/1)
              {monster.usedSkillThisTurn && ' [済]'}
            </button>
          );
        }

        // 上級技
        if (monster.advancedSkill) {
          const canUseAdvanced = monster.charges && monster.charges.length >= 2 && !monster.usedSkillThisTurn;
          buttons.push(
            <button
              key="advanced-skill"
              onClick={() => executeSkill(selectedFieldMonster, 'advanced')}
              disabled={!canUseAdvanced}
              style={getButtonStyle('linear-gradient(135deg, #ff9800 0%, #ffa726 100%)', !canUseAdvanced)}
            >
              上級技 ({monster.charges?.length || 0}/2)
              {monster.usedSkillThisTurn && ' [済]'}
            </button>
          );
        }

        // SPチャージ
        const canCharge = !chargeUsedThisTurn && (monster.charges?.length ?? 0) < 2 && currentActiveSP >= 1;
        buttons.push(
          <button
            key="charge"
            onClick={() => chargeSP(selectedFieldMonster)}
            disabled={!canCharge}
            style={getButtonStyle('linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)', !canCharge)}
          >
            💠 チャージ
          </button>
        );
      }

      // 【壮麗】
      if (canActivateSourei(monster, currentHand) && !monster.soureiUsedThisTurn) {
        buttons.push(
          <button
            key="sourei"
            onClick={() => activateSourei(selectedFieldMonster)}
            style={getButtonStyle('linear-gradient(135deg, #e91e63 0%, #f48fb1 100%)')}
          >
            ✨ 壮麗
          </button>
        );
      }

      // 【未来予知】
      if (canActivateMiraiYochi(monster, phase)) {
        buttons.push(
          <button
            key="miraiyochi"
            onClick={() => activateMiraiYochi(selectedFieldMonster)}
            disabled={monster.usedMiraiYochiThisTurn}
            style={getButtonStyle('linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)', monster.usedMiraiYochiThisTurn)}
          >
            🔮 未来予知
            {monster.usedMiraiYochiThisTurn && ' [済]'}
          </button>
        );
      }

      // 【変幻身】
      if (canActivateHengenmi && canActivateHengenmi(monster, currentActiveSP, currentDeck, phase)) {
        buttons.push(
          <button
            key="hengenmi"
            onClick={() => activateHengenmi(selectedFieldMonster)}
            style={getButtonStyle('linear-gradient(135deg, #8bc34a 0%, #aed581 100%)')}
          >
            🦎 変幻身
          </button>
        );
      }

      // トリガー
      triggers.forEach((trigger, idx) => {
        buttons.push(
          <button
            key={`trigger-${idx}`}
            onClick={() => {
              const triggerContext = createTriggerContext(monster, selectedFieldMonster);
              activateTrigger(trigger, triggerContext);
            }}
            disabled={!trigger.canActivate}
            style={getButtonStyle('linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)', !trigger.canActivate)}
          >
            🌟 {trigger.description}
            {trigger.usedThisTurn && ' [済]'}
          </button>
        );
      });
    }

    // メインフェイズ中の魔法カード選択時
    if (phase === 2 && selectedHandCard && selectedHandCard.type === 'magic') {
      buttons.push(
        <button
          key="magic"
          onClick={useMagicCard}
          style={getButtonStyle('linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)')}
        >
          ✨ 魔法発動
        </button>
      );
    }

    // メインフェイズ中の潜航可能カード選択時
    if (phase === 2 && selectedHandCard && selectedHandCard.type === 'monster' && handleSubmerge) {
      const deepSeaZone = getPlayerDeepSeaZone(currentPlayer);
      const canSubmergeCurrent = deepSeaZone.isUnlocked &&
        canSubmergeCard(currentPlayer, selectedHandCard, turn);

      if (canSubmergeCurrent) {
        buttons.push(
          <button
            key="submerge"
            onClick={() => handleSubmerge(selectedHandCard)}
            style={getButtonStyle('linear-gradient(135deg, #0077be 0%, #00a8e8 100%)')}
          >
            🌊 潜航
          </button>
        );
      }
    }

    // 深海領域モーダル表示ボタン（深海領域が開放されている場合）
    if (phase === 2) {
      const deepSeaZone = getPlayerDeepSeaZone(currentPlayer);
      if (deepSeaZone.isUnlocked && deepSeaZone.cards.length > 0) {
        buttons.push(
          <button
            key="deep-sea-view"
            onClick={() => setShowDeepSeaModal(currentPlayer)}
            style={getButtonStyle('linear-gradient(135deg, #004d7a 0%, #008793 100%)')}
          >
            🌊 深海領域 ({deepSeaZone.cards.length})
          </button>
        );
      }
    }

    // 【犠現】発動
    if (phase === 2 && selectedHandCard && selectedHandCard.type === 'monster' &&
        canActivateGigen(selectedHandCard, currentField)) {
      buttons.push(
        <button
          key="gigen"
          onClick={() => {
            const emptySlot = currentField.findIndex(m => m === null);
            if (emptySlot !== -1) {
              startGigenActivation(selectedHandCard, emptySlot);
            } else {
              startGigenActivation(selectedHandCard, 0);
            }
          }}
          style={getButtonStyle('linear-gradient(135deg, #ff6f00 0%, #ffa726 100%)')}
        >
          🔥 犠現
        </button>
      );
    }

    // 墓地発動
    if (phase === 2 && activatableGraveyardCards.length > 0) {
      activatableGraveyardCards.forEach((card) => {
        buttons.push(
          <button
            key={`graveyard-${card.uniqueId}`}
            onClick={() => activateGraveyardCard(card)}
            style={getButtonStyle('linear-gradient(135deg, #6b4e9e 0%, #8b6bb8 100%)')}
          >
            💀 {card.name}
          </button>
        );
      });
    }

    // バトルフェイズ中のダイレクトアタック
    if (phase === 3 && attackingMonster !== null) {
      const attacker = currentField[attackingMonster];
      const hasOpponentMonster = opponentField.some(m => m !== null);

      // 飛行システムによるダイレクトアタック可否を判定
      const flightDirectStatus = attacker ? getDirectAttackStatus(attacker, opponentField) : { canDirect: false };

      const canDirectAttack = !hasOpponentMonster ||
        (attacker && (attacker as any).canDirectAttackThisTurn) ||
        canIgnoreBlockWithIatsuko() ||
        flightDirectStatus.canDirect;

      if (canDirectAttack) {
        buttons.push(
          <button
            key="direct-attack"
            onClick={handleDirectAttack}
            style={getButtonStyle('linear-gradient(135deg, #ff4444 0%, #ff6666 100%)')}
          >
            ⚔️ ダイレクト
          </button>
        );
      }
    }

    // 刹那詠唱ボタン（相手ターン時）
    // P1の視点で相手ターン（currentPlayer === 2）の時にP1が刹那詠唱可能
    // P2の視点で相手ターン（currentPlayer === 1）の時にP2が刹那詠唱可能
    if (!setsunaPendingActivation && canUseSetsunaMagic()) {
      buttons.push(
        <button
          key="setsuna"
          onClick={startSetsunaMagicMode}
          style={{
            ...getButtonStyle('linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)'),
            animation: 'pulse 2s infinite',
          }}
        >
          ⚡ 刹那詠唱
        </button>
      );
    }

    return buttons;
  };

  // ========================================
  // 固定エリアのボタンをレンダリング
  // ========================================
  const renderFixedButton = () => {
    if (phase === 2) {
      return (
        <button
          onClick={nextPhase}
          style={{
            ...phaseButtonStyle,
            background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
            color: '#fff',
          }}
        >
          バトルへ →
        </button>
      );
    }

    if (phase === 3) {
      return (
        <button
          onClick={nextPhase}
          style={{
            ...phaseButtonStyle,
            background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
            color: '#fff',
          }}
        >
          ターン終了 →
        </button>
      );
    }

    // 相手ターン or その他のフェイズ
    return (
      <div
        style={{
          ...phaseButtonStyle,
          background: 'rgba(40,40,60,0.6)',
          color: '#666',
        }}
      >
        待機中...
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '6px 8px' : '8px 12px',
        background: 'linear-gradient(180deg, rgba(20,20,50,0.95) 0%, rgba(30,20,60,0.98) 100%)',
        borderTop: '2px solid rgba(107,76,230,0.4)',
        flexShrink: 0,
        minHeight: isMobile ? '52px' : '56px',
      }}
    >
      {/* 左側: スクロールエリア */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          paddingRight: '8px',
          msOverflowStyle: 'none',
        }}
        className="action-scroll-area"
      >
        {renderScrollAreaButtons()}
      </div>

      {/* 右側: 固定エリア */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          borderLeft: '1px solid rgba(107,76,230,0.3)',
          paddingLeft: '12px',
          marginLeft: '8px',
        }}
      >
        {renderFixedButton()}
      </div>

      {/* スクロールバー非表示用CSS */}
      <style>{`
        .action-scroll-area::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ActionFooter;
